/**
 * PreSubmissionChecklist Component
 *
 * TurboTax-style final validation before permit submission.
 * Validates all requirements are met and provides go/no-go status.
 *
 * Features:
 * - Document completeness check
 * - Compliance score validation
 * - Conditional requirements verification (NOC, HVHZ, owner-builder)
 * - Clear visual status for each validation item
 * - Expandable details for issues
 *
 * Part of Phase 3.2: Pre-submission Checklist Validation UI
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileCheck,
  Shield,
  AlertCircle,
  Sparkles,
  ArrowRight,
  Loader2,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';

export type ValidationStatus = 'pass' | 'fail' | 'warning' | 'loading';

export interface ValidationItem {
  id: string;
  name: string;
  description: string;
  status: ValidationStatus;
  details?: string;
  autoDetected?: boolean;
  source?: string;
}

export interface ValidationCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  items: ValidationItem[];
  required: boolean;
}

interface PreSubmissionChecklistProps {
  // Document validation
  totalRequiredDocs: number;
  uploadedRequiredDocs: number;
  invalidDocs: number;
  missingDocs: string[];
  // Compliance validation
  complianceScore: number | null;
  complianceStatus: 'pass' | 'fail' | 'needs_review' | null;
  criticalFindings: number;
  warningFindings: number;
  // Conditional requirements
  nocRequired: boolean;
  nocAcknowledged: boolean;
  nocThreshold?: number;
  projectValue?: number;
  hvhzRequired: boolean;
  hvhzDocsUploaded: boolean;
  ownerBuilderRequired: boolean;
  ownerBuilderAcknowledged: boolean;
  // State
  isValidating?: boolean;
  // Actions
  onRunValidation?: () => void;
  onProceedToSubmit?: () => void;
  onFixIssue?: (categoryId: string, itemId: string) => void;
}

export function PreSubmissionChecklist({
  totalRequiredDocs,
  uploadedRequiredDocs,
  invalidDocs,
  missingDocs,
  complianceScore,
  complianceStatus,
  criticalFindings,
  warningFindings,
  nocRequired,
  nocAcknowledged,
  nocThreshold,
  projectValue,
  hvhzRequired,
  hvhzDocsUploaded,
  ownerBuilderRequired,
  ownerBuilderAcknowledged,
  isValidating,
  onRunValidation,
  onProceedToSubmit,
  onFixIssue,
}: PreSubmissionChecklistProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['documents', 'requirements']));

  // Build validation categories
  const categories = useMemo<ValidationCategory[]>(() => {
    const cats: ValidationCategory[] = [];

    // Document Completeness
    const docItems: ValidationItem[] = [];
    const allDocsUploaded = uploadedRequiredDocs >= totalRequiredDocs && totalRequiredDocs > 0;
    const noInvalidDocs = invalidDocs === 0;

    docItems.push({
      id: 'doc_complete',
      name: 'Required Documents',
      description: `${uploadedRequiredDocs} of ${totalRequiredDocs} required documents uploaded`,
      status: allDocsUploaded ? 'pass' : 'fail',
      details: missingDocs.length > 0 ? `Missing: ${missingDocs.join(', ')}` : undefined,
    });

    if (invalidDocs > 0) {
      docItems.push({
        id: 'doc_valid',
        name: 'Document Validation',
        description: `${invalidDocs} document(s) need attention`,
        status: 'fail',
        details: 'Some documents were flagged as invalid or need review',
      });
    }

    cats.push({
      id: 'documents',
      name: 'Document Completeness',
      icon: <FileCheck className="h-5 w-5" />,
      items: docItems,
      required: true,
    });

    // Compliance Check
    if (complianceScore !== null) {
      const complianceItems: ValidationItem[] = [];

      complianceItems.push({
        id: 'compliance_score',
        name: 'Compliance Score',
        description: `${complianceScore}% compliance with jurisdiction requirements`,
        status: complianceStatus === 'pass' ? 'pass' : complianceStatus === 'needs_review' ? 'warning' : 'fail',
        autoDetected: true,
        source: 'Compliance engine analysis',
      });

      if (criticalFindings > 0) {
        complianceItems.push({
          id: 'critical_findings',
          name: 'Critical Issues',
          description: `${criticalFindings} critical finding(s) must be resolved`,
          status: 'fail',
          details: 'Critical issues will likely cause rejection',
        });
      }

      if (warningFindings > 0) {
        complianceItems.push({
          id: 'warning_findings',
          name: 'Warnings',
          description: `${warningFindings} warning(s) should be reviewed`,
          status: 'warning',
          details: 'Warnings may cause delays or requests for information',
        });
      }

      cats.push({
        id: 'compliance',
        name: 'Compliance Validation',
        icon: <Shield className="h-5 w-5" />,
        items: complianceItems,
        required: true,
      });
    }

    // Conditional Requirements
    const reqItems: ValidationItem[] = [];

    if (nocRequired) {
      reqItems.push({
        id: 'noc',
        name: 'Notice of Commencement (NOC)',
        description: nocAcknowledged
          ? 'NOC requirement acknowledged'
          : `Required for projects over $${(nocThreshold || 2500).toLocaleString()}`,
        status: nocAcknowledged ? 'pass' : 'warning',
        autoDetected: true,
        source: projectValue
          ? `Project value: $${projectValue.toLocaleString()}`
          : 'Florida NOC threshold rule',
        details: nocAcknowledged ? undefined : 'You must file an NOC before starting work',
      });
    }

    if (hvhzRequired) {
      reqItems.push({
        id: 'hvhz',
        name: 'HVHZ Documentation',
        description: hvhzDocsUploaded
          ? 'High Velocity Hurricane Zone docs uploaded'
          : 'HVHZ product approvals required',
        status: hvhzDocsUploaded ? 'pass' : 'fail',
        autoDetected: true,
        source: 'Property located in HVHZ',
        details: hvhzDocsUploaded
          ? undefined
          : 'Florida Product Approvals (NOA/FBC) are required for HVHZ',
      });
    }

    if (ownerBuilderRequired) {
      reqItems.push({
        id: 'owner_builder',
        name: 'Owner-Builder Disclosure',
        description: ownerBuilderAcknowledged
          ? 'Owner-builder status acknowledged'
          : 'Owner-builder disclosure required',
        status: ownerBuilderAcknowledged ? 'pass' : 'warning',
        autoDetected: true,
        source: 'Owner-builder status indicated',
        details: ownerBuilderAcknowledged
          ? undefined
          : 'Special requirements apply for owner-builder permits',
      });
    }

    if (reqItems.length > 0) {
      cats.push({
        id: 'requirements',
        name: 'Conditional Requirements',
        icon: <AlertCircle className="h-5 w-5" />,
        items: reqItems,
        required: false,
      });
    }

    return cats;
  }, [
    totalRequiredDocs,
    uploadedRequiredDocs,
    invalidDocs,
    missingDocs,
    complianceScore,
    complianceStatus,
    criticalFindings,
    warningFindings,
    nocRequired,
    nocAcknowledged,
    nocThreshold,
    projectValue,
    hvhzRequired,
    hvhzDocsUploaded,
    ownerBuilderRequired,
    ownerBuilderAcknowledged,
  ]);

  // Calculate overall status
  const overallStatus = useMemo(() => {
    let hasFailures = false;
    let hasWarnings = false;
    let totalItems = 0;
    let passedItems = 0;

    categories.forEach((cat) => {
      cat.items.forEach((item) => {
        totalItems++;
        if (item.status === 'pass') passedItems++;
        if (item.status === 'fail' && cat.required) hasFailures = true;
        if (item.status === 'warning') hasWarnings = true;
      });
    });

    const progress = totalItems > 0 ? (passedItems / totalItems) * 100 : 0;

    return {
      canSubmit: !hasFailures,
      hasWarnings,
      progress,
      passedItems,
      totalItems,
    };
  }, [categories]);

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getCategoryStatus = (category: ValidationCategory): ValidationStatus => {
    const hasFailure = category.items.some((i) => i.status === 'fail');
    const hasWarning = category.items.some((i) => i.status === 'warning');
    if (hasFailure) return 'fail';
    if (hasWarning) return 'warning';
    return 'pass';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-primary" />
              Pre-Submission Checklist
            </CardTitle>
            <CardDescription>
              Review these items before submitting your permit application
            </CardDescription>
          </div>
          {overallStatus.canSubmit ? (
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Ready to Submit
            </Badge>
          ) : (
            <Badge variant="destructive">
              <XCircle className="h-3.5 w-3.5 mr-1" />
              Issues Found
            </Badge>
          )}
        </div>

        {/* Progress bar */}
        <div className="space-y-1 pt-2">
          <Progress
            value={overallStatus.progress}
            className={cn(
              'h-2',
              overallStatus.canSubmit && 'bg-green-100 [&>div]:bg-green-500'
            )}
          />
          <p className="text-xs text-muted-foreground text-right">
            {overallStatus.passedItems} of {overallStatus.totalItems} checks passed
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Validation Categories */}
        {categories.map((category) => {
          const catStatus = getCategoryStatus(category);
          const isExpanded = expandedCategories.has(category.id);

          return (
            <Collapsible key={category.id} open={isExpanded}>
              <CollapsibleTrigger
                onClick={() => toggleCategory(category.id)}
                className="w-full"
              >
                <div
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg border transition-colors',
                    catStatus === 'pass' && 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800',
                    catStatus === 'warning' && 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800',
                    catStatus === 'fail' && 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <div
                      className={cn(
                        'p-1.5 rounded',
                        catStatus === 'pass' && 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
                        catStatus === 'warning' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
                        catStatus === 'fail' && 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                      )}
                    >
                      {category.icon}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{category.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {category.items.filter((i) => i.status === 'pass').length} of{' '}
                        {category.items.length} items passed
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {category.required && catStatus === 'fail' && (
                      <Badge variant="destructive" className="text-xs">
                        Required
                      </Badge>
                    )}
                    {catStatus === 'pass' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                    {catStatus === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                    {catStatus === 'fail' && <XCircle className="h-5 w-5 text-red-600" />}
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="mt-2 ml-6 space-y-2">
                  {category.items.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border',
                        item.status === 'pass' && 'bg-green-50/50 border-green-100 dark:bg-green-950/10 dark:border-green-900',
                        item.status === 'warning' && 'bg-amber-50/50 border-amber-100 dark:bg-amber-950/10 dark:border-amber-900',
                        item.status === 'fail' && 'bg-red-50/50 border-red-100 dark:bg-red-950/10 dark:border-red-900'
                      )}
                    >
                      {/* Status Icon */}
                      <div className="shrink-0 mt-0.5">
                        {item.status === 'pass' && (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                        {item.status === 'warning' && (
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                        )}
                        {item.status === 'fail' && <XCircle className="h-4 w-4 text-red-600" />}
                        {item.status === 'loading' && (
                          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{item.name}</p>
                          {item.autoDetected && (
                            <ConfidenceBadge
                              level="high"
                              source={item.source || 'System detected'}
                              showLabel={false}
                            />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.description}
                        </p>
                        {item.details && (
                          <p
                            className={cn(
                              'text-xs mt-1.5 flex items-start gap-1',
                              item.status === 'fail' && 'text-red-600 dark:text-red-400',
                              item.status === 'warning' && 'text-amber-600 dark:text-amber-400'
                            )}
                          >
                            <Info className="h-3 w-3 shrink-0 mt-0.5" />
                            {item.details}
                          </p>
                        )}
                      </div>

                      {/* Fix Action */}
                      {(item.status === 'fail' || item.status === 'warning') && onFixIssue && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0 h-7 text-xs"
                          onClick={() => onFixIssue(category.id, item.id)}
                        >
                          Fix
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            {isValidating ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Validating...</span>
              </div>
            ) : onRunValidation ? (
              <Button variant="outline" size="sm" onClick={onRunValidation}>
                Re-run Validation
              </Button>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {overallStatus.hasWarnings && overallStatus.canSubmit && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Warnings present
              </p>
            )}
            <Button
              disabled={!overallStatus.canSubmit || isValidating}
              onClick={onProceedToSubmit}
            >
              {overallStatus.canSubmit ? (
                <>
                  Proceed to Submit
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Resolve Issues First
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PreSubmissionChecklist;
